/**
 * WonderLabs R2 — Curiosity to Confidence (experimental presentation only).
 *
 * Blocks remain authored and saved by JourneyPageRenderer. This module only
 * decides which existing blocks are shown together. Missing / invalid plans
 * fall back to a single legacy beat. Never use this as a completion authority.
 */

import { ALL_ACTIVITY_BEAT_PLANS } from "./wonderLabsAllActivityBeatPlans";
import { ROBOT_ARTIST_EXPLORERS_BEAT_PLANS } from "./robotArtistExplorersBeatPlans";
import { FOOD_DELIVERY_ROBOT_BEAT_PLANS } from "./foodDeliveryRobotBeatPlans";
import { MAGNETWORKS_BEAT_PLANS } from "./magnetworksBeatPlans";

// Default ON for self-guided WonderLabs; allow an explicit URL-based R1 fallback.
// Callers must continue to scope this to self-guided mode.
export const resolveWonderLabsLearningBeatsFlag = (search = "") =>
  new URLSearchParams(search).get("ff_wonderlabs_learning_beats") !== "0";

export const isWonderLabsLearningBeatsEnabled = () => {
  if (typeof window === "undefined") return false;
  try {
    return resolveWonderLabsLearningBeatsFlag(window.location.search);
  } catch (_) {
    return false;
  }
};

// Four educator/UX demonstration plans checked in Stage A2. These override the
// full-coverage Stage B proposal where the same activity appears. Composite
// story_prelude blocks are also handled inside JourneyStoryPrelude.
export const REVIEWED_BEAT_PLANS = Object.freeze({
  // Each child-facing beat now has ONE focused instructional purpose.
  // The earlier 'inspect' group included the introduction, pictures AND the
  // 10-item parts checklist on the same screen; that was R1 with a new label.
  "wind-car-apply:step5-story": [
    { id: "prepare", title: "Get ready to inspect your kit", kind: "prepare",
      blockIds: ["step5-story"] },
    { id: "photos", title: "Inspect the actual kit and guide", kind: "inspect",
      blockIds: ["readiness-gallery"] },
    { id: "parts", title: "Confirm the parts you have", kind: "check",
      blockIds: ["parts-readiness"] },
    { id: "safety", title: "Check that assembly is safe", kind: "safety",
      blockIds: ["safety-checks"] },
    { id: "predict", title: "Predict one likely failure", kind: "predict",
      blockIds: ["failure-prediction"] },
  ],
  "conductive-dough-explore:step2-story": [
    { id: "intro", title: "Find the materials mystery", kind: "prepare",
      blockIds: ["step2-story"] },
    { id: "predict", title: "Predict which materials carry current", kind: "predict",
      blockIds: ["material-prediction"] },
    { id: "explore", title: "Test your thinking in 3D", kind: "inspect",
      blockIds: ["mainmenu-pwo", "materials-pwo"] },
    { id: "compare", title: "Compare the material evidence", kind: "observe",
      blockIds: ["material-gallery"] },
  ],
  "electric-spiral-understand:bridge-story": [
    { id: "intro", title: "Explore the two energy jobs", kind: "prepare",
      blockIds: ["bridge-story"] },
    { id: "explain", title: "Compare light and motion", kind: "explain",
      blockIds: ["same-different"] },
    { id: "evidence", title: "Look for visual evidence", kind: "observe",
      blockIds: ["comparison-gallery"] },
  ],
  "robot-artist-understand:step4-story": [
    { id: "intro", title: "Discover the motion question", kind: "prepare",
      blockIds: ["step4-story"] },
    { id: "predict", title: "Predict where motion starts", kind: "predict",
      blockIds: ["energy-start-prediction"] },
    { id: "observe", title: "Examine the evidence", kind: "observe",
      blockIds: ["energy-evidence-gallery", "energy-intro-video"] },
    { id: "explore", title: "Investigate energy in 3D", kind: "inspect",
      blockIds: ["energy-forms-pwo", "battery-pwo"] },
  ],
});

export const validateLearningBeatPlan = (activity, beats) => {
  const expected = Array.isArray(activity?.blockIds) ? activity.blockIds : [];
  if (!Array.isArray(beats) || beats.length < 1) return { valid: false, reason: "No beats" };
  const all = beats.flatMap((beat) => Array.isArray(beat.blockIds) ? beat.blockIds : []);
  if (all.length !== expected.length || new Set(all).size !== all.length ||
      expected.some((id, index) => all[index] !== id)) {
    return { valid: false, reason: "Blocks must appear once, in authored order" };
  }
  if (beats.some((beat) => !beat.id || !beat.title || !beat.kind || !beat.blockIds?.length)) {
    return { valid: false, reason: "Missing beat metadata" };
  }
  if (new Set(beats.map((beat) => beat.id)).size !== beats.length) {
    return { valid: false, reason: "Duplicate beat ID" };
  }
  return { valid: true };
};

export const resolveLearningBeats = (stepId, activity) => {
  if (!activity?.blockIds?.length) return [];
  const key = `${stepId}:${activity.id}`;
  const curated = REVIEWED_BEAT_PLANS[key] || ROBOT_ARTIST_EXPLORERS_BEAT_PLANS[key] || FOOD_DELIVERY_ROBOT_BEAT_PLANS[key] || MAGNETWORKS_BEAT_PLANS[key];
  if (curated && validateLearningBeatPlan(activity, curated).valid) return curated;
  const fullCoverage = ALL_ACTIVITY_BEAT_PLANS[key];
  if (fullCoverage && validateLearningBeatPlan(activity, fullCoverage).valid) return fullCoverage;
  return [{
    id: "whole-activity",
    title: activity.title,
    kind: "existing-activity",
    blockIds: [...activity.blockIds],
  }];
};

// An authored response can gate movement to the next beat. Informational
// blocks have no completion items and advance explicitly on learner click.
// This does NOT change phase or activity completion rules.
export const beatCanAdvance = (beat, completionState) => {
  if (!beat) return false;
  const ids = new Set(beat.blockIds);
  const assigned = (completionState?.items || []).filter((item) =>
    item.targetBlockId && ids.has(item.targetBlockId));
  return assigned.every((item) => Boolean(item.complete));
};

export const beatProgress = (beats, cursor) => ({
  index: Math.max(0, Math.min(Math.max(0, beats.length - 1), Number(cursor) || 0)),
  count: beats.length,
});

// Ground the on-screen next-step guidance in the existing authored completion
// rules, not a separate visit/click flag. Never expose future answer values.
export const learningBeatRequirement = (beat, completionState) => {
  if (!beat) return null;
  const ids = new Set(beat.blockIds || []);
  const required = (completionState?.items || []).filter((item) =>
    item.targetBlockId && ids.has(item.targetBlockId));
  const missing = required.find((item) => !item.complete);
  return missing ? { label: missing.label || "Finish this task", complete: false } : null;
};
